import type { SlotDesign } from './design-data';

/**
 * Card sides the customer did not design.
 *
 * Invitations offer a back side next to the front. A single-sided design
 * leaves the back untouched, and that back must not reach the print shop as a
 * blank page. A side is blank when nothing visible was placed on it: no
 * visible layer and no gradient. Its colour and background image are the
 * paper's (the paper picker paints them), not the customer's design.
 */
export function isBlankSlot(slot: SlotDesign): boolean {
  const visible = (slot.layers ?? []).some((l) => !l.hidden);
  return !visible && slot.background?.gradient == null;
}

/**
 * The sides to send to the cart: every side the definition declares, minus
 * optional ones the customer left blank. A required side is always kept, so
 * the backend's "missing required slot" check still guards the order.
 */
export function slotsToPrint(
  slots: SlotDesign[],
  definitionSlots: Array<{ key: string; required?: boolean }>,
): SlotDesign[] {
  const byKey = new Map(definitionSlots.map((s) => [s.key, s]));
  return slots.filter((s) => {
    const spec = byKey.get(s.slotKey);
    if (!spec) return false;
    return spec.required !== false || !isBlankSlot(s);
  });
}

/**
 * Give a restored draft every side the definition has: sides the draft lacks
 * (an invitation draft saved before the back existed) start blank on the
 * definition's page, in the definition's order.
 */
export function withAllSides(
  slots: SlotDesign[],
  definitionSlots: Array<{
    key: string;
    printSpec: { widthMm: number; heightMm: number; bleedMm: number; safeMm?: number };
  }>,
): SlotDesign[] {
  const byKey = new Map(slots.map((s) => [s.slotKey, s]));
  return definitionSlots.map(
    (d) =>
      byKey.get(d.key) ?? {
        slotKey: d.key,
        canvas: {
          widthMm: d.printSpec.widthMm,
          heightMm: d.printSpec.heightMm,
          bleedMm: d.printSpec.bleedMm,
          safeMm: d.printSpec.safeMm,
        },
        background: { color: '#ffffff' },
        layers: [],
      },
  );
}
